import React from 'react'
import { useGetDashboardStatsQuery, useGetRecentActivityQuery, useGetSystemAlertsQuery } from '../store/api/dashboardApi'
import StatsCard from '../components/StatsCard'
import ActivityFeed from '../components/ActivityFeed'
import AlertsList from '../components/AlertsList'

const Dashboard: React.FC = () => {
  const { data: stats, isLoading: statsLoading } = useGetDashboardStatsQuery()
  const { data: activities, isLoading: activitiesLoading } = useGetRecentActivityQuery(10)
  const { data: alerts, isLoading: alertsLoading } = useGetSystemAlertsQuery()

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome to the Motionode Admin Panel</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Users"
          value={stats?.totalUsers || 0}
          subtitle={`${stats?.activeUsers || 0} active`}
          trend="+12%"
          trendUp={true}
        />
        <StatsCard
          title="Organizations"
          value={stats?.totalOrganizations || 0}
          subtitle="Active organizations"
          trend="+5%"
          trendUp={true}
        />
        <StatsCard
          title="Projects"
          value={stats?.totalProjects || 0}
          subtitle={`${stats?.totalRequests || 0} deliverables`}
          trend="+8%"
          trendUp={true}
        />
        <StatsCard
          title="New Deliverables"
          value={stats?.newRequestsToday || 0}
          subtitle="Today"
          trend="+15%"
          trendUp={true}
        />
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Deliverables"
          value={stats?.totalRequests || 0}
          subtitle="All time"
          trend="+20%"
          trendUp={true}
        />
        <StatsCard
          title="Total Tickets"
          value={stats?.totalTickets || 0}
          subtitle="All time"
          trend="+18%"
          trendUp={true}
        />
        <StatsCard
          title="Team Members"
          value={stats?.totalMembers || 0}
          subtitle={`${stats?.totalGuests || 0} guests`}
          trend="+10%"
          trendUp={true}
        />
        <StatsCard
          title="Monthly Activity"
          value={stats?.monthlyRevenue || 0}
          subtitle="New projects"
          trend="+25%"
          trendUp={true}
        />
      </div>

      {/* System Health */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Health</h2>
        <div className="flex items-center space-x-4">
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${stats?.systemHealth || 0}%` }}
            />
          </div>
          <span className="text-sm font-medium text-gray-700">
            {stats?.systemHealth || 0}%
          </span>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          {activitiesLoading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : (
            <ActivityFeed activities={activities || []} />
          )}
        </div>

        {/* System Alerts */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">System Alerts</h2>
          {alertsLoading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : (
            <AlertsList alerts={alerts || []} />
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard 